import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Select,
  Switch,
  TimePicker,
  Typography,
  Space,
  Button,
  Spin,
  Row,
  Col,
  Empty,
  App,
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../services/api';
import * as authApi from '../../services/authApi';
import { User } from '../../types';

const { Text } = Typography;
const { Option } = Select;

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIME_FORMAT = 'HH:mm';
const DEFAULT_START = '08:00';
const DEFAULT_END = '18:00';

interface TechnicianScheduleRow {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface DayState {
  is_active: boolean;
  start: Dayjs;
  end: Dayjs;
}

const buildDefaultDays = (): DayState[] =>
  DAY_LABELS.map(() => ({
    is_active: false,
    start: dayjs(DEFAULT_START, TIME_FORMAT),
    end: dayjs(DEFAULT_END, TIME_FORMAT),
  }));

interface TechnicianHoursSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Owner-facing per-technician weekly-hours editor.
 *
 * Lets staff pick a technician/owner and set their working hours for each day
 * of the week. These hours drive Simplified Scheduling slot generation
 * (technician_schedules). Reuses the existing shop-scoped, staff-guarded route
 * GET/PUT /api/admin/schedules/technicians/:userId.
 */
const TechnicianHoursSettingsModal: React.FC<TechnicianHoursSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const { message } = App.useApp();
  const [staff, setStaff] = useState<User[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [days, setDays] = useState<DayState[]>(buildDefaultDays());
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load staff list whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadStaff = async () => {
      setLoadingStaff(true);
      try {
        const users = await authApi.getAllUsers({ status: 'active' });
        if (cancelled) return;
        // Technicians + owners (and advisors) all may have working hours.
        const filtered = users.filter((u) =>
          ['owner', 'technician', 'advisor'].includes(u.role),
        );
        setStaff(filtered);
      } catch (err) {
        if (!cancelled) message.error('Failed to load staff list');
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    };
    loadStaff();
    return () => {
      cancelled = true;
    };
  }, [open, message]);

  // Reset selection when the modal is closed.
  useEffect(() => {
    if (!open) {
      setSelectedUserId(undefined);
      setDays(buildDefaultDays());
    }
  }, [open]);

  const loadSchedule = useCallback(
    async (userId: number) => {
      setLoadingSchedule(true);
      try {
        const res = await api.get<TechnicianScheduleRow[]>(`/schedules/technicians/${userId}`);
        const rows = res.data || [];
        const next = buildDefaultDays();
        for (const row of rows) {
          if (row.day_of_week < 0 || row.day_of_week > 6) continue;
          next[row.day_of_week] = {
            is_active: Boolean(row.is_active),
            start: dayjs(row.start_time || DEFAULT_START, TIME_FORMAT),
            end: dayjs(row.end_time || DEFAULT_END, TIME_FORMAT),
          };
        }
        setDays(next);
      } catch (err) {
        message.error('Failed to load schedule');
        setDays(buildDefaultDays());
      } finally {
        setLoadingSchedule(false);
      }
    },
    [message],
  );

  const handleSelectUser = (userId: number) => {
    setSelectedUserId(userId);
    loadSchedule(userId);
  };

  const updateDay = (index: number, patch: Partial<DayState>) => {
    setDays((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      message.warning('Select a technician first');
      return;
    }
    setSaving(true);
    try {
      const payload: TechnicianScheduleRow[] = days.map((d, dayOfWeek) => ({
        day_of_week: dayOfWeek,
        start_time: (d.start || dayjs(DEFAULT_START, TIME_FORMAT)).format(TIME_FORMAT),
        end_time: (d.end || dayjs(DEFAULT_END, TIME_FORMAT)).format(TIME_FORMAT),
        is_active: d.is_active,
      }));
      await api.put(`/schedules/technicians/${selectedUserId}`, payload);
      message.success('Schedule saved');
      onClose();
    } catch (err) {
      message.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <ClockCircleOutlined />
          Technician Hours
        </Space>
      }
      okText="Save"
      onOk={handleSave}
      confirmLoading={saving}
      okButtonProps={{ disabled: !selectedUserId }}
      width={520}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>Technician</Text>
          <Select
            placeholder="Select a technician"
            value={selectedUserId}
            onChange={handleSelectUser}
            loading={loadingStaff}
            style={{ width: '100%', marginTop: 4 }}
            showSearch
            optionFilterProp="children"
          >
            {staff.map((u) => (
              <Option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.role})
              </Option>
            ))}
          </Select>
        </div>

        {!selectedUserId ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a technician to set their weekly hours"
          />
        ) : loadingSchedule ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <div>
            {days.map((day, index) => (
              <Row
                key={index}
                gutter={8}
                align="middle"
                style={{ marginBottom: 12 }}
                wrap={false}
              >
                <Col flex="34px">
                  <Switch
                    checked={day.is_active}
                    onChange={(checked) => updateDay(index, { is_active: checked })}
                    size="small"
                  />
                </Col>
                <Col flex="80px">
                  <Text style={{ opacity: day.is_active ? 1 : 0.45 }}>
                    {DAY_LABELS[index].slice(0, 3)}
                  </Text>
                </Col>
                <Col flex="auto">
                  <TimePicker
                    value={day.start}
                    onChange={(value) =>
                      updateDay(index, { start: value || dayjs(DEFAULT_START, TIME_FORMAT) })
                    }
                    format={TIME_FORMAT}
                    minuteStep={15}
                    allowClear={false}
                    disabled={!day.is_active}
                    style={{ width: '100%' }}
                    inputReadOnly
                  />
                </Col>
                <Col flex="12px" style={{ textAlign: 'center' }}>
                  <Text type="secondary">–</Text>
                </Col>
                <Col flex="auto">
                  <TimePicker
                    value={day.end}
                    onChange={(value) =>
                      updateDay(index, { end: value || dayjs(DEFAULT_END, TIME_FORMAT) })
                    }
                    format={TIME_FORMAT}
                    minuteStep={15}
                    allowClear={false}
                    disabled={!day.is_active}
                    style={{ width: '100%' }}
                    inputReadOnly
                  />
                </Col>
              </Row>
            ))}
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default TechnicianHoursSettingsModal;
